import type * as React from "react";
import { type MutableRefObject, type ReactElement, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useOnMount } from "../common/hooks/useOnMount";
import { getNextUID } from "~/utils/uid";

export function createModalServiceRoot() {
  return <ModalServiceRoot />;
}

export function openModal(id: string, creator: (closeFun: () => void) => ReactElement): () => void {
  let modal: ReactElement | null = null;
  const closeFun = () => closeExactModal(modal!);
  modal = creator(closeFun);
  setChildrenList && setChildrenList((prevList) => prevList.concat({ id, cmp: modal! }));
  return closeFun;
}

function closeExactModal(el: ReactElement): void {
  setChildrenList && setChildrenList((prevList) => prevList.filter((item) => item.cmp !== el));
}

export function closeModalById(id: string): void {
  setChildrenList && setChildrenList((prevList) => prevList.filter((item) => item.id !== id));
}

export interface ConfirmationModalProps {
  onConfirm: () => void;
  onCancel: () => void;
}

export function requestConfirmation(fn: (props: ConfirmationModalProps) => ReactElement): Promise<boolean> {
  return new Promise((resolve) => {
    openModal("confirmation-" + getNextUID(), (close) => {
      const makeCb = (confirmed: boolean) => () => {
        close();
        resolve(confirmed);
      };
      return fn({ onConfirm: makeCb(true), onCancel: makeCb(false) });
    });
  });
}

interface ModalEntry {
  cmp: ReactElement;
  id: string;
}

let setChildrenList: React.Dispatch<React.SetStateAction<ModalEntry[]>> | null = null;

export const ModalServiceRoot = () => {
  const [children, setChildren] = useState<ModalEntry[]>([]);
  const ref: MutableRefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

  useOnMount(() => {
    if (setChildrenList !== null) {
      console.warn("ModalServiceRoot already exists");
      return;
      // throw new Error("Don't render multiple ModalServiceRoots");
    }
    setChildrenList = setChildren;
    ref.current = document.createElement("div");
    document.body.appendChild(ref.current);

    return () => {
      setChildrenList = null;
      if (ref.current) {
        document.body.removeChild(ref.current);
      }
    };
  });

  if (!ref.current) {
    return null;
  }

  return createPortal(
    children.map((i) => i.cmp),
    ref.current
  );
};
